/**
 * API client. Same-origin `/api` (vite dev proxy / nginx in Docker), cookie
 * sessions, CSRF token attached to every state-changing request.
 */
export interface ApiErrorBody {
  code: string;
  message: string;
  correlationId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId: string | undefined;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.status = status;
    this.code = body.code;
    this.correlationId = body.correlationId;
  }
}

let csrfToken: string | null = null;

async function fetchCsrfToken(): Promise<string> {
  const res = await fetch('/api/v1/auth/csrf', { credentials: 'include' });
  if (!res.ok) throw new ApiError(res.status, await parseError(res));
  const body = (await res.json()) as { csrfToken: string };
  csrfToken = body.csrfToken;
  return body.csrfToken;
}

async function parseError(res: Response): Promise<ApiErrorBody> {
  try {
    const body = (await res.json()) as { error?: ApiErrorBody };
    if (body.error?.code) return body.error;
  } catch {
    // fall through to the generic message
  }
  return { code: 'HTTP_' + res.status, message: `request failed with status ${res.status}` };
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' });
  if (!res.ok) throw new ApiError(res.status, await parseError(res));
  return (await res.json()) as T;
}

export async function apiSend<T>(
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const doSend = async (token: string | null): Promise<Response> =>
    fetch(path, {
      method,
      credentials: 'include',
      headers: {
        // A JSON content-type with no body is rejected by the API.
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { 'x-csrf-token': token } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

  let token = csrfToken;
  if (!token) token = await fetchCsrfToken().catch(() => null);
  let res = await doSend(token);
  if (res.status === 403) {
    const err = await parseError(res.clone());
    if (err.code === 'CSRF_FAILED') {
      // Session rotated (e.g. re-login): refresh the token once and retry.
      token = await fetchCsrfToken();
      res = await doSend(token);
    }
  }
  if (!res.ok) throw new ApiError(res.status, await parseError(res));
  return (await res.json()) as T;
}

/** Login has no session yet, so it is exempt from CSRF token handling. */
export async function apiLogin(email: string, mfa: boolean): Promise<void> {
  const res = await fetch('/api/v1/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, mfa }),
  });
  if (!res.ok) throw new ApiError(res.status, await parseError(res));
  csrfToken = null;
}

export async function apiLogout(): Promise<void> {
  await apiSend('POST', '/api/v1/auth/logout');
  csrfToken = null;
}
